import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { differenceInMonths, format } from 'date-fns';

import { LocalizationContext } from '@cvt/contexts';

import { AuthContext } from '@modules/Auth/contexts/AuthContext';

import { useUserCrud } from '../hooks/useUserCrud';

type Props = {
  open: boolean;
  onClose: () => void;
}

export const ResetAccountDialog: React.FC<Props> = ({ open, onClose }) => {
  const { dictionary } = React.useContext(LocalizationContext);
  const { user } = React.useContext(AuthContext);
  const { resetAccount } = useUserCrud();

  const message = React.useMemo(() => {
    if (!user) return null;
    if (user.lastResetDate) {
      return dictionary.users.resetAccount.dialog.subsequent(format(user.lastResetDate, 'dd MMM yyyy'));
    }
    if (user.createdAt) {
      return dictionary.users.resetAccount.dialog.firstTime(differenceInMonths(new Date(), user.createdAt));
    }
    return null;
  }, [user, dictionary]);

  const onReset = React.useCallback(async () => {
    try {
      await resetAccount.mutateAsync();
    } catch {
      // Error feedback is surfaced by the mutation's onError handler.
    } finally {
      onClose();
    }
  }, [resetAccount, onClose]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { elevation: 0, square: true } }}
    >
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1" align="center" fontWeight={600}>
            {dictionary.users.resetAccount.dialog.title}
          </Typography>
          <Typography variant="body2" align="center">
            {message}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack width="100%" spacing={2} direction="row" justifyContent="space-between">
          <Button type="button" variant="text" color="inherit" onClick={onClose}>
            {dictionary.dialogs.buttonCancel}
          </Button>
          <LoadingButton
            onClick={onReset}
            loading={resetAccount.isPending}
            variant="contained"
            color="primary"
          >
            {dictionary.users.resetAccount.dialog.buttonReset}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
